<template>
  <div
    :class="{
      'bm-mini-tabs': miniTabs,
      'bm-tabs': !miniTabs,
    }"
  >
    <v-divider v-if="!miniTabs" />
    <v-tabs
      :value="value"
      :grow="!miniTabs"
      show-arrows
      :height="getTabHeight"
      :hide-slider="miniTabs"
      active-class="activeTab"
      v-bind="$attrs"
      @change="onValueChange"
    >
      <v-tabs-slider :color="disabled ? 'error' : 'primary'" />

      <v-tab
        v-for="item in filteredItems"
        :key="item.id"
        :to="item.route"
        class="bm-tab text-none"
        :data-qa="item.id"
        :disabled="disabled"
      >
        {{ item.label }}
        <v-avatar
          v-if="item.notification"
          class="bm-notify"
          size="24"
          color="brand03"
        >
        </v-avatar>
      </v-tab>

      <slot />
    </v-tabs>
    <v-divider v-if="!miniTabs" />
  </div>
</template>

<script lang="ts">
import type BmTabItem from "@/components/controls/tabs/BmTabItem";
import type { PropType } from "vue";
import { defineComponent } from "vue";

/**
 * Tab control that can be used with a router or without.
 *
 * 'items' must be an array of BmTabItem's.
 * 'value' will be an index number for non-router tabs and the full relative url for router tabs.
 */
export default defineComponent({
  name: "BmTabs",
  inheritAttrs: false,
  props: {
    value: {
      type: [Number, String] as PropType<number | string>,
      required: false,
    },
    items: {
      type: Array as PropType<readonly BmTabItem[]>,
      default: () => [],
    },
    miniTabs: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    getTabHeight(): any {
      return this.miniTabs ? "2em" : "";
    },
    filteredItems(): any {
      return this.items.filter((item) => !item.hidden);
    },
  },
  methods: {
    onValueChange(val: any) {
      if (typeof val === "number") {
        this.$emit("new-tab", this.items[val]);
      }
      this.$emit("input", val);
    },
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/colours"
@import "src/sass/typography"

.bm-tabs, .bm-mini-tabs
  margin-bottom: 16px

// Remove the tab bar background
.bm-tabs :deep(.theme--light.v-tabs__bar, .theme--dark.v-tabs__bar)
  background-color: transparent

// Make the tab text bigger and link text colour
.bm-tab :deep(a.v-tabs__item)
  @include bm-headline
  color: $bm-color-link-text !important

.bm-notify
  margin-left: 5px

// Mini Tab Styling
.bm-mini-tabs div.activeTab, .bm-mini-tabs a.activeTab
    background-color: $bm-color-brand01
    border: 1px solid transparent !important
    color: $bm-color-background01 !important

.bm-mini-tabs .bm-tab
    border-top: 1px solid $bm-color-brand01
    border-bottom: 1px solid $bm-color-brand01
    border-left: 1px solid $bm-color-brand01
    color: $bm-color-brand01 !important
    // border-radius: 2px
    @include bm-body

.bm-mini-tabs :deep(.v-slide-group__content .bm-tab:first-child)
  border-radius: 3px 0 0 3px !important

.bm-mini-tabs :deep(.v-slide-group__content .bm-tab:last-child)
  border-right: 1px solid $bm-color-brand01
  border-radius: 0 3px 3px 0 !important
</style>
